<template>
    <b-modal
        :id="modalId"
        v-model="syncVisible"
        modal-class="sl-export-modal"
        content-class="sl-export-modal__content border-0"
        body-class="sl-export-modal__body"
        centered
        hide-header
        hide-footer
        @hidden="$emit('input', false)"
    >
        <button type="button" class="sl-export-modal__close" :aria-label="$t('lbl_EMPBE_SalesLead_Export_Cancel')" @click="close">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
                <path d="M6 6L18 18M18 6L6 18" stroke="rgba(0,0,0,0.6)" stroke-width="2" stroke-linecap="round"/>
            </svg>
        </button>
        <h2 class="sl-export-modal__title">{{ exportTitle }}</h2>

        <section class="sl-export-modal__section">
            <h3 class="sl-export-modal__label">{{ $t('lbl_EMPBE_SalesLead_Export_SectionRange') }}</h3>
            <div class="sl-export-modal__radios">
                <label class="sl-export-modal__radio-row">
                    <input type="radio" class="sl-export-modal__radio" v-model="rangeMode" value="all" />
                    <span class="sl-export-modal__radio-label">{{ exportAllOptionLabel }}</span>
                </label>
                <label class="sl-export-modal__radio-row">
                    <input type="radio" class="sl-export-modal__radio" v-model="rangeMode" value="date" />
                    <span class="sl-export-modal__radio-label">{{ $t('lbl_EMPBE_SalesLead_Export_DateRange') }}</span>
                </label>
                <div class="sl-export-modal__dates" :class="{ 'sl-export-modal__dates--disabled': rangeMode !== 'date' }">
                    <div class="sl-export-modal__date-field">
                        <span class="sl-export-modal__date-hint">{{ $t('lbl_EMPBE_SalesLead_Export_From') }}</span>
                        <div
                            class="sl-export-modal__date-input-wrap"
                            role="button"
                            tabindex="0"
                            @click="handleDateWrapClick('fromDate', $event)"
                            @keydown.enter.prevent="handleDateWrapClick('fromDate', $event)"
                            @keydown.space.prevent="handleDateWrapClick('fromDate', $event)"
                        >
                            <span v-if="!fromDate" class="sl-export-modal__date-placeholder" aria-hidden="true">
                                {{ $t('lbl_EMPBE_DateFormatYYYYMMDD') }}
                            </span>
                            <input
                                type="text"
                                class="sl-export-modal__date-input"
                                :class="{ 'sl-export-modal__date-input--empty': !fromDate }"
                                :value="fromDate"
                                readonly
                                :disabled="rangeMode !== 'date'"
                                :aria-label="$t('lbl_EMPBE_SalesLead_Export_From')"
                            />
                            <input
                                ref="fromDateInput"
                                type="date"
                                v-model="fromDate"
                                :disabled="rangeMode !== 'date'"
                                tabindex="-1"
                                aria-hidden="true"
                            />
                            <img
                                class="sl-export-modal__date-icon"
                                src="https://cdn.adsalecdn.com/EMP/2-0/SalesLead/Event.svg"
                                width="18"
                                height="18"
                                alt=""
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                    <div class="sl-export-modal__date-field">
                        <span class="sl-export-modal__date-hint">{{ $t('lbl_EMPBE_SalesLead_Export_To') }}</span>
                        <div
                            class="sl-export-modal__date-input-wrap"
                            role="button"
                            tabindex="0"
                            @click="handleDateWrapClick('toDate', $event)"
                            @keydown.enter.prevent="handleDateWrapClick('toDate', $event)"
                            @keydown.space.prevent="handleDateWrapClick('toDate', $event)"
                        >
                            <span v-if="!toDate" class="sl-export-modal__date-placeholder" aria-hidden="true">
                                {{ $t('lbl_EMPBE_DateFormatYYYYMMDD') }}
                            </span>
                            <input
                                type="text"
                                class="sl-export-modal__date-input"
                                :class="{ 'sl-export-modal__date-input--empty': !toDate }"
                                :value="toDate"
                                readonly
                                :disabled="rangeMode !== 'date'"
                                :aria-label="$t('lbl_EMPBE_SalesLead_Export_To')"
                            />
                            <input
                                ref="toDateInput"
                                type="date"
                                v-model="toDate"
                                :disabled="rangeMode !== 'date'"
                                tabindex="-1"
                                aria-hidden="true"
                            />
                            <img
                                class="sl-export-modal__date-icon"
                                src="https://cdn.adsalecdn.com/EMP/2-0/SalesLead/Event.svg"
                                width="18"
                                height="18"
                                alt=""
                                aria-hidden="true"
                            />
                        </div>
                    </div>
                </div>
                <p v-if="dateError" class="sl-export-modal__date-error" role="alert">
                    {{ dateError }}
                </p>
            </div>
        </section>

        <section class="sl-export-modal__section">
            <h3 class="sl-export-modal__label">{{ $t('lbl_EMPBE_SalesLead_Export_SectionFormat') }}</h3>
            <div class="sl-export-modal__radios">
                <label class="sl-export-modal__radio-row">
                    <input type="radio" class="sl-export-modal__radio" v-model="format" value="xlsx" />
                    <span class="sl-export-modal__radio-label">{{ $t('lbl_EMPBE_SalesLead_Export_FormatXlsx') }}</span>
                </label>
                <label v-if="allowCsv" class="sl-export-modal__radio-row">
                    <input type="radio" class="sl-export-modal__radio" v-model="format" value="csv" />
                    <span class="sl-export-modal__radio-label">{{ $t('lbl_EMPBE_SalesLead_Export_FormatCsv') }}</span>
                </label>
            </div>
        </section>

        <div class="sl-export-modal__footer">
            <button type="button" class="sl-export-modal__cancel" @click="close">
                {{ $t('lbl_EMPBE_SalesLead_Export_Cancel') }}
            </button>
            <button type="button" class="sl-export-modal__submit" t_label="Emp_saleslead_export_submit" :disabled="exporting" @click="submit">
                {{ $t('lbl_EMPBE_SalesLead_Export_Submit') }}
            </button>
        </div>
    </b-modal>
</template>

<script>
import { formatSalesLeadDate } from '@/_helpers/formatSalesLeadDate';

export default {
    name: 'SalesLeadExportModal',
    model: {
        prop: 'value',
        event: 'input',
    },
    props: {
        modalId: { type: String, default: 'sales-lead-export-modal' },
        value: { type: Boolean, default: false },
        exportTitle: { type: String, default: '' },
        exportAllOptionLabel: { type: String, default: '' },
        exporting: { type: Boolean, default: false },
        allowCsv: { type: Boolean, default: true },
    },
    data() {
        return {
            rangeMode: 'all',
            fromDate: '',
            toDate: '',
            format: 'xlsx',
            dateError: '',
        };
    },
    computed: {
        syncVisible: {
            get() { return this.value; },
            set(v) { this.$emit('input', Boolean(v)); },
        },
    },
    watch: {
        value(open) {
            if (open) {
                this.rangeMode = 'all';
                this.fromDate = '';
                this.toDate = '';
                this.format = 'xlsx';
                this.dateError = '';
            }
        },
        allowCsv(allowed) {
            if (!allowed && this.format === 'csv') {
                this.format = 'xlsx';
            }
        },
        rangeMode(mode) {
            this.dateError = '';
            if (mode === 'all') {
                this.fromDate = '';
                this.toDate = '';
            } else if (mode === 'date') {
                this.applyDefaultDateRange();
            }
        },
        fromDate() {
            this.dateError = '';
        },
        toDate() {
            this.dateError = '';
        },
    },
    methods: {
        handleDateWrapClick(field, event) {
            const refName = field === 'fromDate' ? 'fromDateInput' : 'toDateInput';
            if (this.rangeMode !== 'date') {
                this.rangeMode = 'date';
                this.$nextTick(() => this.handleDateWrapClick(field));
                return;
            }
            if (event && event.target === this.$refs[refName]) {
                return;
            }
            const input = this.$refs[refName];
            if (!input || input.disabled) return;
            if (typeof input.showPicker === 'function') {
                try {
                    input.showPicker();
                    return;
                } catch (e) {}
            }
            input.focus();
            input.click();
        },
        close() {
            this.syncVisible = false;
        },
        formatDateInput(date) {
            const y = date.getFullYear();
            const m = String(date.getMonth() + 1).padStart(2, '0');
            const d = String(date.getDate()).padStart(2, '0');
            return `${y}-${m}-${d}`;
        },
        applyDefaultDateRange() {
            const to = new Date();
            const from = new Date();
            from.setMonth(from.getMonth() - 1);
            this.toDate = this.formatDateInput(to);
            this.fromDate = this.formatDateInput(from);
        },
        normalizeExportDateField(field) {
            const normalized = formatSalesLeadDate(this[field]);
            if (normalized) {
                this[field] = normalized;
            }
        },
        validateDateRange() {
            if (this.rangeMode !== 'date') {
                this.dateError = '';
                return true;
            }
            this.normalizeExportDateField('fromDate');
            this.normalizeExportDateField('toDate');
            const from = String(this.fromDate || '').trim();
            const to = String(this.toDate || '').trim();
            if (!from || !to) {
                this.dateError = this.$t('lbl_EMPBE_SalesLead_Export_DateRequired');
                return false;
            }
            if (from > to) {
                this.dateError = this.$t('lbl_EMPBE_SalesLead_Export_DateRangeInvalid');
                return false;
            }
            this.dateError = '';
            return true;
        },
        submit() {
            if (!this.validateDateRange()) return;
            this.$emit('export', {
                rangeMode: this.rangeMode,
                fromDate: this.fromDate,
                toDate: this.toDate,
                format: this.format,
            });
        },
    },
};
</script>

<style lang="scss">
.sl-export-modal {
    .modal-content {
        position: relative;
        border: none;
        border-radius: 12px;
        box-shadow: 0 12px 40px rgba(15, 23, 42, 0.16);
    }

    .modal-body {
        position: static;
    }

    .modal-dialog {
        max-width: 554px;
        width: min(554px, calc(100vw - 32px));
        margin: 1.75rem auto;
    }
}

.sl-export-modal .modal-body.sl-export-modal__body {
    overflow: visible;
    padding: 24px;
}

.sl-export-modal__content {
    box-sizing: border-box;
    width: 100%;
    padding: 0;
}

.sl-export-modal__close {
    position: absolute;
    top: 10px;
    right: 10px;
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    margin: 0;
    padding: 4px;
    border: none;
    background: transparent;
    cursor: pointer;
}

.sl-export-modal__title {
    margin: 0 0 16px;
    padding: 0 36px 0 0;
    text-align: left;
    color: rgba(0, 0, 0, 0.8);
    font-family: Inter, sans-serif;
    font-size: 20px;
    font-weight: 700;
    line-height: 26px;
}

.sl-export-modal__section {
    margin-bottom: 24px;
}

.sl-export-modal__label {
    margin: 0 0 12px;
    color: rgba(0, 0, 0, 0.8);
    font-family: Inter, sans-serif;
    font-size: 16px;
    font-weight: 700;
    line-height: 21px;
}

.sl-export-modal__radios {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    max-width: 506px;
}

.sl-export-modal__radio-row {
    display: flex;
    align-items: flex-start;
    gap: 4px;
    margin: 0;
    cursor: pointer;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 400;
    line-height: 18.2px;
    color: rgba(0, 0, 0, 0.8);
}

.sl-export-modal__radio {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    margin: 0;
    accent-color: #7366ff;
}

.sl-export-modal__radio-label {
    flex: 1;
    min-width: 0;
    padding-top: 3px;
    font-weight: 400;
    line-height: 18.2px;
}

.sl-export-modal__dates {
    display: flex;
    align-items: flex-start;
    gap: 12px;
    width: 100%;
    max-width: 506px;
    padding-left: 28px;
    box-sizing: border-box;

    &--disabled {
        opacity: 0.5;
    }
}

.sl-export-modal__date-field {
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: 4px;
}

.sl-export-modal__date-hint {
    font-family: Inter, sans-serif;
    font-size: 12px;
    font-weight: 400;
    line-height: 16px;
    color: rgba(0, 0, 0, 0.5);
}

.sl-export-modal__date-error {
    margin: 8px 0 0 28px;
    max-width: 506px;
    font-family: Inter, sans-serif;
    font-size: 12px;
    line-height: 16px;
    color: #dc3545;
}

.sl-export-modal__date-input-wrap {
    position: relative;
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-height: 40px;
    box-sizing: border-box;
    padding: 8px;
    border-radius: 4px;
    border: 1px solid rgba(0, 0, 0, 0.1);
    background: #fff;
    cursor: pointer;
}

.sl-export-modal__date-placeholder {
    display: block;
    position: absolute;
    left: 8px;
    right: 34px;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0.15px;
    color: rgba(0, 0, 0, 0.4);
    pointer-events: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.sl-export-modal__date-input {
    flex: 1;
    min-width: 0;
    width: 100%;
    height: 24px;
    box-sizing: border-box;
    padding: 0;
    border: none;
    border-radius: 0;
    font-family: Inter, sans-serif;
    font-size: 14px;
    line-height: 21px;
    letter-spacing: 0.15px;
    color: rgba(0, 0, 0, 0.8);
    background: transparent;
    cursor: pointer;

    &--empty {
        color: transparent;
    }

    &:disabled {
        cursor: not-allowed;
        pointer-events: none;
    }
}

.sl-export-modal__date-input-wrap input[type="date"] {
    position: absolute;
    width: 1px;
    height: 1px;
    margin: -1px;
    padding: 0;
    border: 0;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    pointer-events: none;
}

.sl-export-modal__date-icon {
    flex-shrink: 0;
    width: 18px;
    height: 18px;
    pointer-events: none;
}

.sl-export-modal__footer {
    display: flex;
    justify-content: flex-end;
    align-items: center;
    gap: 16px;
    width: 100%;
    margin-top: 8px;
}

.sl-export-modal__cancel {
    padding: 15px 24px;
    border: none;
    border-radius: 4px;
    background: transparent;
    color: rgba(0, 18, 26, 0.6);
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;
}

.sl-export-modal__submit {
    padding: 15px 24px;
    border: none;
    border-radius: 4px;
    background: #7366ff;
    color: #fff;
    font-family: Inter, sans-serif;
    font-size: 14px;
    font-weight: 700;
    cursor: pointer;

    &:disabled {
        opacity: 0.6;
        cursor: not-allowed;
    }
}

@media screen and (max-width: 480px) {
    .sl-export-modal {
        .modal-content {
            border-radius: 16px;
        }

        .modal-dialog {
            max-width: 360px;
            width: calc(100vw - 32px);
            margin: 1rem auto;
        }
    }

    .sl-export-modal__dates {
        flex-direction: column;
        gap: 8px;
    }

    .sl-export-modal__date-field {
        width: 100%;
        flex: none;
    }

    .sl-export-modal__date-hint {
        display: none;
    }

}
</style>
